import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import './PasswordInput.css';

/**
 * PasswordInput — text input with a built-in show/hide-password toggle.
 *
 * Why this component exists:
 *   The default `<input type="password">` masks every character with dots,
 *   which makes it easy to typo a long password without realising it.
 *   The toggle button on the right lets the user reveal what they typed
 *   to double-check before submitting.
 *
 * Same prop surface as a normal text input: value, onChange, placeholder,
 * autoComplete, required, autoFocus. The component manages the
 * shown/hidden state internally — defaults to hidden.
 */
function PasswordInput({
  value,
  onChange,
  placeholder = '••••••••',
  required = false,
  autoFocus = false,
  autoComplete = 'current-password',
  id,
  name = 'password',
  minLength,
}) {
  const [shown, setShown] = useState(false);

  return (
    <div className="password-input">
      <input
        id={id}
        name={name}
        type={shown ? 'text' : 'password'}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        minLength={minLength}
        className="password-input__field"
      />
      <button
        type="button"
        className="password-input__toggle"
        onClick={() => setShown((s) => !s)}
        // The toggle isn't a navigable element — keeps the form's tab order clean.
        tabIndex={-1}
        aria-label={shown ? 'Hide password' : 'Show password'}
        title={shown ? 'Hide password' : 'Show password'}
      >
        {shown ? <FiEyeOff /> : <FiEye />}
      </button>
    </div>
  );
}

export default PasswordInput;
